import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useAuthStore } from '../../stores/auth';
import { api } from '../../api/client';
import type { PassSummary, RedemptionRecord } from '@sundkortid/shared';

function formatRelativeDate(dateStr: string) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins} mín síðan`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours} klst síðan`;
  const days = Math.floor(hours / 24);
  if (days === 1) return 'í gær';
  return `${days} dögum síðan`;
}

export function DashboardPage() {
  const user = useAuthStore((s) => s.user);
  const [passes, setPasses] = useState<PassSummary[]>([]);
  const [history, setHistory] = useState<RedemptionRecord[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get<PassSummary[]>('/passes').catch(() => [] as PassSummary[]),
      api.get<RedemptionRecord[]>('/redemptions/history').catch(() => [] as RedemptionRecord[]),
    ]).then(([p, h]) => {
      setPasses(p);
      setHistory(h);
      setIsLoading(false);
    });
  }, []);

  const activePasses = passes.filter((p) => p.status === 'active');
  const totalRedemptions = history.filter((r) => !r.isReversed).length;
  const lastVisit = history.length > 0 ? history[0] : null;
  const remainingEntries = activePasses.reduce((sum, p) => {
    if (p.totalEntries !== null) return sum + (p.totalEntries - p.usedEntries);
    return sum;
  }, 0);
  const hasUnlimited = activePasses.some((p) => p.totalEntries === null);
  const firstActivePass = activePasses[0];
  const recentHistory = history.slice(0, 4);

  const firstName = user?.fullName?.split(' ')[0] ?? '';

  return (
    <div>
      {/* Greeting */}
      <div className="mb-8">
        <p className="text-xs font-semibold tracking-[0.25em] uppercase text-primary-400 mb-2">
          Yfirlit
        </p>
        <h1 className="text-3xl md:text-4xl font-title font-bold text-primary-900">
          Hæ, {firstName}
        </h1>
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8 stagger-children">
        <StatCard
          label="Virk kort"
          value={isLoading ? '–' : String(activePasses.length)}
          icon={
            <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 8.25h19.5M2.25 9h19.5m-16.5 5.25h6m-6 2.25h3m-3.75 3h15a2.25 2.25 0 0 0 2.25-2.25V6.75A2.25 2.25 0 0 0 19.5 4.5h-15a2.25 2.25 0 0 0-2.25 2.25v10.5A2.25 2.25 0 0 0 4.5 19.5Z" />
            </svg>
          }
        />
        <StatCard
          label="Heimsóknir"
          value={isLoading ? '–' : String(totalRedemptions)}
          icon={
            <svg className="w-5 h-5 text-secondary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75 11.25 15 15 9.75M21 12a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          }
        />
        <StatCard
          label="Síðasta heimsókn"
          value={isLoading ? '–' : lastVisit ? formatRelativeDate(lastVisit.scannedAt) : '—'}
          small
          icon={
            <svg className="w-5 h-5 text-accent-700" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6h4.5m4.5 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          }
        />
        <StatCard
          label="Miðar eftir"
          value={isLoading ? '–' : hasUnlimited ? '∞' : String(remainingEntries)}
          icon={
            <svg className="w-5 h-5 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 6v.75m0 3v.75m0 3v.75m0 3V18m-9-5.25h5.25M7.5 15h3M3.375 5.25c-.621 0-1.125.504-1.125 1.125v3.026a2.999 2.999 0 0 1 0 5.198v3.026c0 .621.504 1.125 1.125 1.125h17.25c.621 0 1.125-.504 1.125-1.125v-3.026a2.999 2.999 0 0 1 0-5.198V6.375c0-.621-.504-1.125-1.125-1.125H3.375Z" />
            </svg>
          }
        />
      </div>

      {/* Quick actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-10">
        {firstActivePass ? (
          <Link
            to={`/passes/${firstActivePass.id}/qr`}
            className="group relative overflow-hidden rounded-2xl bg-gradient-primary p-6 text-white shadow-lg shadow-primary/20 hover:shadow-xl transition-all duration-200"
          >
            <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-white/10 -translate-y-8 translate-x-8" />
            <div className="relative">
              <h3 className="text-xl font-title font-bold mb-1">Sýna QR kóða</h3>
              <p className="text-white/70 text-sm">Opnaðu kóðann þinn til að skanna</p>
            </div>
            <svg className="absolute right-5 bottom-5 w-6 h-6 text-white/40 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
            </svg>
          </Link>
        ) : (
          <div className="rounded-2xl border-2 border-dashed border-gray-200 p-6 flex items-center justify-center">
            <p className="text-sm text-gray-400">Engin virk kort — kauptu kort til að byrja</p>
          </div>
        )}

        <Link
          to="/buy"
          className="group rounded-2xl border-2 border-primary-200 bg-white p-6 hover:border-primary-300 hover:shadow-md transition-all duration-200"
        >
          <h3 className="text-xl font-title font-bold text-primary-900 mb-1">Kaupa kort</h3>
          <p className="text-sm text-gray-400">Skoðaðu verðskrá og kauptu nýtt kort</p>
          <svg className="mt-3 w-5 h-5 text-primary-400 group-hover:translate-x-1 transition-transform" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5 21 12m0 0-7.5 7.5M21 12H3" />
          </svg>
        </Link>
      </div>

      {/* Recent activity */}
      {recentHistory.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-title font-bold text-primary-900">Nýleg notkun</h2>
            <Link to="/history" className="text-sm font-medium text-primary-500 hover:text-primary-700 transition-colors">
              Sjá alla sögu →
            </Link>
          </div>
          <div className="space-y-3 stagger-children">
            {recentHistory.map((r) => (
              <div
                key={r.id}
                className="flex items-center gap-4 bg-white rounded-xl border border-gray-100 px-4 py-3 shadow-sm"
              >
                <div className={`w-8 h-8 rounded-full flex items-center justify-center shrink-0 ${
                  r.isReversed ? 'bg-gray-100' : 'bg-green-50'
                }`}>
                  {r.isReversed ? (
                    <svg className="w-4 h-4 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                    </svg>
                  ) : (
                    <svg className="w-4 h-4 text-green-600" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
                    </svg>
                  )}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-900 truncate">{r.poolName}</p>
                  <p className="text-xs text-gray-400">{formatRelativeDate(r.scannedAt)}</p>
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  r.isReversed ? 'bg-gray-100 text-gray-500' : 'bg-green-50 text-green-700'
                }`}>
                  {r.isReversed ? 'Afturkallað' : 'Gilt'}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-white rounded-xl border border-gray-100 p-4 h-16 animate-pulse" />
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, icon, small }: { label: string; value: string; icon: React.ReactNode; small?: boolean }) {
  return (
    <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-5">
      <div className="w-9 h-9 rounded-xl bg-primary-50/80 flex items-center justify-center mb-3">
        {icon}
      </div>
      <p className={`font-bold text-primary-900 ${small ? 'text-lg' : 'text-3xl'}`}>{value}</p>
      <p className="text-xs text-gray-400 mt-0.5">{label}</p>
    </div>
  );
}
